import { useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import PageSeo from "@/components/PageSeo";

type GalleryImage = {
  id: number;
  src: string;
  alt: string;
  span: string;
};

const images: GalleryImage[] = [
  { id: 1, src: "/images/gallery/gallery-15.webp", alt: "DJ am Pult", span: "col-span-2 row-span-2" },
  { id: 2, src: "/images/gallery/gallery-2.webp", alt: "Freundinnen mit Drinks", span: "col-span-1 row-span-1" },
  { id: 3, src: "/images/gallery/gallery-10.webp", alt: "Barkeeperin", span: "col-span-1 row-span-2" },
  { id: 4, src: "/images/gallery/gallery-13.webp", alt: "Barkeeper mixt Drink", span: "col-span-1 row-span-1" },
  { id: 5, src: "/images/gallery/gallery-1.webp", alt: "Freunde feiern", span: "col-span-1 row-span-1" },
  { id: 6, src: "/images/gallery/gallery-16.webp", alt: "Barkeeperin lächelt", span: "col-span-1 row-span-1" },
  { id: 7, src: "/images/gallery/gallery-4.webp", alt: "Volle Tanzfläche", span: "col-span-2 row-span-1" },
  { id: 8, src: "/images/gallery/gallery-12.webp", alt: "Gast mit Drink", span: "col-span-1 row-span-1" },
  { id: 9, src: "/images/gallery/gallery-8.webp", alt: "Stylisches Paar", span: "col-span-1 row-span-1" },
  { id: 10, src: "/images/gallery/gallery-19.webp", alt: "DJ lacht", span: "col-span-1 row-span-1" },
  { id: 11, src: "/images/gallery/gallery-14.webp", alt: "Freundinnen", span: "col-span-1 row-span-1" },
  { id: 12, src: "/images/gallery/gallery-11.webp", alt: "Halloween Barkeeper", span: "col-span-1 row-span-1" },
  { id: 13, src: "/images/gallery/gallery-9.webp", alt: "Party-Stimmung", span: "col-span-2 row-span-1" },
  { id: 14, src: "/images/gallery/gallery-3.webp", alt: "Freunde an der Bar", span: "col-span-1 row-span-1" },
  { id: 15, src: "/images/gallery/gallery-17.webp", alt: "Bar-Team", span: "col-span-1 row-span-1" },
  { id: 16, src: "/images/gallery/gallery-18.webp", alt: "DJ Pyrotik", span: "col-span-2 row-span-2" },
  { id: 17, src: "/images/gallery/gallery-7.webp", alt: "Gäste tanzen", span: "col-span-1 row-span-1" },
  { id: 18, src: "/images/gallery/gallery-5.webp", alt: "Photobox", span: "col-span-1 row-span-1" },
  { id: 19, src: "/images/gallery/gallery-6.webp", alt: "Im Club", span: "col-span-1 row-span-1" },
];

const GalleryPage = () => {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const closeLightbox = () => setLightboxIndex(null);
  const goPrev = () => setLightboxIndex((i) => (i !== null ? (i - 1 + images.length) % images.length : null));
  const goNext = () => setLightboxIndex((i) => (i !== null ? (i + 1) % images.length : null));

  const galleryJsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Galerie – Luther Club Kaiserslautern",
    description: "Bildergalerie aus dem Luther Club Kaiserslautern: DJs, Gäste, Bar und Tanzfläche.",
    url: "https://lutherclub.de/gallery",
    hasPart: images.map((img) => ({
      "@type": "ImageObject",
      contentUrl: `https://lutherclub.de${img.src}`,
      name: img.alt,
    })),
  };

  return (
    <div className="pt-20">
      <PageSeo
        title="Galerie – Luther Club Kaiserslautern"
        description="Bildergalerie aus dem Luther Club Kaiserslautern: DJs, Gäste, Bar und Tanzfläche – Einblicke in unsere Partynächte."
        path="/gallery"
        jsonLd={galleryJsonLd}
      />
      {/* Hero */}
      <div className="relative py-20 px-4 text-center">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(184,164,76,0.06)_0%,_transparent_70%)]" />
        <h1 className="relative text-4xl md:text-5xl font-bold text-primary mb-3">
          Galerie
        </h1>
        <p className="relative font-[Montserrat] text-sm text-muted-foreground tracking-[0.2em] uppercase">
          Einblicke in den Luther Club
        </p>
      </div>

      {/* Masonry-style grid */}
      <div className="max-w-6xl mx-auto px-4 pb-24">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3 auto-rows-[160px] md:auto-rows-[200px]">
          {images.map((img, index) => (
            <div
              key={img.id}
              className={`${img.span} relative group overflow-hidden rounded-lg cursor-pointer`}
              onClick={() => setLightboxIndex(index)}
            >
              <img
                src={img.src}
                alt={img.alt}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/15 transition-colors duration-300" />
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-[100] bg-background/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-8"
          onClick={closeLightbox}
        >
          <button
            className="absolute top-6 right-6 text-foreground hover:text-primary transition-colors z-10"
            onClick={closeLightbox}
          >
            <X className="w-8 h-8" />
          </button>
          <button
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 text-foreground hover:text-primary transition-colors z-10"
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
          >
            <ChevronLeft className="w-10 h-10" />
          </button>
          <button
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 text-foreground hover:text-primary transition-colors z-10"
            onClick={(e) => { e.stopPropagation(); goNext(); }}
          >
            <ChevronRight className="w-10 h-10" />
          </button>
          <img
            src={images[lightboxIndex].src}
            alt={images[lightboxIndex].alt}
            className="max-w-full max-h-[85vh] object-contain rounded"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default GalleryPage;
